// @ts-nocheck
/**
 * Content Management Admin Console
 * Comprehensive content management interface using Carbon Design System
 * Provides marketing team with visual tools for KV-based content management
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  InlineNotification,
  Loading,
  Breadcrumb,
  BreadcrumbItem,
  Header,
  HeaderContainer,
  HeaderName,
  HeaderMenuButton,
  HeaderNavigation,
  HeaderMenuItem,
  HeaderMenu,
  SideNav,
  SideNavItems,
  SideNavLink,
  Theme,
  SkeletonText,
  SkeletonPlaceholder,
} from '@carbon/react';
import {
  Document,
  Edit,
  View,
  Settings,
  Analytics,
  Checkmark,
  Warning,
  ErrorFilled,
  Information,
  Notification,
  Home,
} from '@carbon/icons-react';

// Import sub-components
import ContentLibraryBrowser from './ContentLibraryBrowser';
import ContentEditor from './ContentEditor';
import VersionHistoryInterface from './VersionHistoryInterface';
import ApprovalWorkflowDashboard from './ApprovalWorkflowDashboard';
import ABTestConfigWizard from './ABTestConfigWizard';
import PersonalizationEditor from './PersonalizationEditor';
import CacheManagementInterface from './CacheManagementInterface';
import ContentPreviewModal from './ContentPreviewModal';

// Import interfaces
import type {
  ContentManagementAdminProps,
  ContentLibraryItem,
  WorkflowNotification,
  ContentPreviewOptions,
  AnalyticsIntegration,
} from '../../../shared/interfaces/ContentManagementAdmin';

// Import permission service
import { marketingPermissionsService, type MarketingPermissions } from '../../services/marketingPermissions';
import { useAuthStore } from '../../store/authStore';

interface ContentManagementAdminState {
  activeTab: string;
  sideNavExpanded: boolean;
  loading: boolean;
  error: string | null;
  notifications: WorkflowNotification[];
  selectedContentId?: string;
  previewModalOpen: boolean;
  previewOptions?: ContentPreviewOptions;
  stats: AdminStats;
  permissions: MarketingPermissions | null;
  permissionError: string | null;
}

interface AdminStats {
  totalContent: number;
  pendingApproval: number;
  activeTests: number;
  cacheHitRate: number;
}

const ContentManagementAdmin: React.FC<ContentManagementAdminProps> = ({
  className = '',
  onContentChange,
  onWorkflowUpdate,
  integrationMode = 'standalone',
}) => {
  const { user } = useAuthStore();

  const [state, setState] = useState<ContentManagementAdminState>({
    activeTab: 'library',
    sideNavExpanded: false,
    loading: true,
    error: null,
    notifications: [],
    previewModalOpen: false,
    permissions: null,
    permissionError: null,
    stats: {
      totalContent: 0,
      pendingApproval: 0,
      activeTests: 0,
      cacheHitRate: 0,
    },
  });

  // Load initial data and permissions
  useEffect(() => {
    loadPermissions();
    loadDashboardData();
    loadNotifications();
  }, [user]);

  const loadPermissions = async () => {
    if (!user?.user_id) {
      setState(prev => ({
        ...prev,
        permissionError: 'User not authenticated',
        loading: false,
      }));
      return;
    }

    try {
      const permissions = await marketingPermissionsService.checkMarketingPermissions(user.user_id);

      // Check if user has any marketing access
      const hasAnyAccess = Object.values(permissions).some(permission => permission === true);

      if (!hasAnyAccess) {
        setState(prev => ({
          ...prev,
          permissionError: 'You do not have permission to access marketing management features',
          loading: false,
          permissions,
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        permissions,
        permissionError: null,
      }));
    } catch (error) {
      console.error('Failed to load marketing permissions:', error);
      setState(prev => ({
        ...prev,
        permissionError: 'Failed to verify permissions. Please try again.',
        loading: false,
      }));
    }
  };

  const loadDashboardData = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // TEMPORARY: Test bypass for marketing portal testing
      const testBypass = window.location.search.includes('test=admin');
      if (testBypass) {
        const mockStats = {
          totalContent: 150,
          pendingApprovals: 8,
          activeTests: 3,
          cacheHitRate: 94.2,
        };
        const mockNotifications = [
          {
            id: '1',
            type: 'approval_request',
            message: 'New content pending approval',
            timestamp: new Date().toISOString(),
          },
          { id: '2', type: 'test_completed', message: 'A/B test completed', timestamp: new Date().toISOString() },
        ];

        setState(prev => ({
          ...prev,
          dashboardStats: mockStats,
          notifications: mockNotifications,
          loading: false,
        }));
        return;
      }

      // Load dashboard statistics
      const [statsResponse, notificationsResponse] = await Promise.all([
        fetch('/api/v1/content-management/dashboard/stats'),
        fetch('/api/v1/content-management/notifications'),
      ]);

      if (!statsResponse.ok) {
        throw new Error('Failed to load dashboard statistics');
      }

      // Add error handling for JSON parsing
      let stats, notifications;
      try {
        stats = await statsResponse.json();
      } catch (parseError) {
        console.error('Failed to parse stats JSON:', parseError);
        throw new Error('Invalid response format from dashboard stats API');
      }

      try {
        notifications = notificationsResponse.ok ? await notificationsResponse.json() : [];
      } catch (parseError) {
        console.error('Failed to parse notifications JSON:', parseError);
        notifications = []; // Use empty array if notifications fail
      }

      setState(prev => ({
        ...prev,
        stats: stats.data,
        notifications: notifications.data || [],
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load dashboard data',
        loading: false,
      }));
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await fetch('/api/v1/content-management/notifications?unread=true');
      if (response.ok) {
        const data = await response.json();
        setState(prev => ({
          ...prev,
          notifications: data.notifications || [],
        }));
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const handleTabChange = (selectedIndex: number) => {
    const tabs = ['library', 'editor', 'versions', 'approvals', 'abtests', 'personalization', 'cache'];
    setState(prev => ({
      ...prev,
      activeTab: tabs[selectedIndex] || 'library',
    }));
  };

  const handleContentSelect = (contentId: string) => {
    setState(prev => ({
      ...prev,
      selectedContentId: contentId,
    }));
  };

  const handlePreviewContent = (options: ContentPreviewOptions) => {
    setState(prev => ({
      ...prev,
      previewModalOpen: true,
      previewOptions: options,
    }));
  };

  const handleNotificationDismiss = (notificationId: string) => {
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.filter(n => n.id !== notificationId),
    }));
  };

  const handleWorkflowAction = async (workflowId: string, action: string, comment?: string) => {
    try {
      const response = await fetch('/api/v1/content-management/workflow/review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workflowId,
          userId: 'current_user', // TODO: Get from auth context
          decision: action,
          comments: comment,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to process workflow action');
      }

      const result = await response.json();
      onWorkflowUpdate?.(workflowId, result.workflowStatus);

      // Refresh notifications and stats
      loadNotifications();
      loadDashboardData();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Workflow action failed',
      }));
    }
  };

  const handleContentUpdate = (contentId: string, content: any) => {
    onContentChange?.(contentId, content);
    loadDashboardData(); // Refresh stats
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'approval_request':
        return <Notification size={16} />;
      case 'content_approved':
        return <Checkmark size={16} />;
      case 'content_rejected':
        return <ErrorFilled size={16} />;
      case 'test_completed':
        return <Analytics size={16} />;
      case 'cache_issue':
        return <Warning size={16} />;
      default:
        return <Information size={16} />;
    }
  };

  const renderDashboardHeader = () => (
    <div
      style={{
        marginBottom: '2rem',
        borderBottom: '1px solid var(--cds-border-subtle)',
        paddingBottom: '1rem',
      }}
    >
      <Grid>
        <Column lg={12} md={6} sm={4}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  margin: 0,
                  color: 'var(--cds-text-01)',
                }}
              >
                Content Management Admin
              </h1>
              <p
                style={{
                  color: 'var(--cds-text-02)',
                  fontSize: '1.125rem',
                  marginTop: '0.5rem',
                  margin: 0,
                }}
              >
                Comprehensive content management for marketing team
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Button
                kind="secondary"
                renderIcon={Analytics}
                onClick={() => window.open('/marketing-analytics', '_blank')}
              >
                View Analytics
              </Button>
              <Button
                kind="primary"
                renderIcon={Edit}
                onClick={() => setState(prev => ({ ...prev, activeTab: 'editor' }))}
              >
                New Content
              </Button>
            </div>
          </div>
        </Column>

        <Column lg={4} md={2} sm={2}>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                textAlign: 'center',
                padding: '1rem',
                background: 'var(--cds-layer-01)',
                borderRadius: '4px',
                minWidth: '120px',
              }}
            >
              <div style={{ fontSize: '2rem', fontWeight: 600, color: 'var(--cds-text-01)' }}>
                {state.stats.totalContent}
              </div>
              <div style={{ color: 'var(--cds-text-02)', fontSize: '0.875rem' }}>Total Content</div>
            </div>

            <div
              style={{
                textAlign: 'center',
                padding: '1rem',
                background: 'var(--cds-layer-01)',
                borderRadius: '4px',
                minWidth: '120px',
              }}
            >
              <div style={{ fontSize: '2rem', fontWeight: 600, color: 'var(--cds-support-03)' }}>
                {state.stats.pendingApproval}
              </div>
              <div style={{ color: 'var(--cds-text-02)', fontSize: '0.875rem' }}>Pending Approval</div>
            </div>

            <div
              style={{
                textAlign: 'center',
                padding: '1rem',
                background: 'var(--cds-layer-01)',
                borderRadius: '4px',
                minWidth: '120px',
              }}
            >
              <div style={{ fontSize: '2rem', fontWeight: 600, color: 'var(--cds-support-02)' }}>
                {state.stats.activeTests}
              </div>
              <div style={{ color: 'var(--cds-text-02)', fontSize: '0.875rem' }}>Active Tests</div>
            </div>

            <div
              style={{
                textAlign: 'center',
                padding: '1rem',
                background: 'var(--cds-layer-01)',
                borderRadius: '4px',
                minWidth: '120px',
              }}
            >
              <div style={{ fontSize: '2rem', fontWeight: 600, color: 'var(--cds-support-04)' }}>
                {state.stats.cacheHitRate}%
              </div>
              <div style={{ color: 'var(--cds-text-02)', fontSize: '0.875rem' }}>Cache Hit Rate</div>
            </div>
          </div>
        </Column>
      </Grid>
    </div>
  );

  const renderNotifications = () => {
    if (state.notifications.length === 0) return null;

    return (
      <div style={{ marginBottom: '2rem' }}>
        {state.notifications.slice(0, 3).map(notification => (
          <InlineNotification
            key={notification.id}
            kind={
              notification.type === 'content_approved'
                ? 'success'
                : notification.type === 'content_rejected'
                  ? 'error'
                  : notification.type === 'cache_issue'
                    ? 'warning'
                    : 'info'
            }
            title={notification.title}
            subtitle={notification.message}
            onCloseButtonClick={() => handleNotificationDismiss(notification.id)}
            style={{ marginBottom: '1rem' }}
          />
        ))}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (state.activeTab) {
      case 'library':
        return (
          <ContentLibraryBrowser
            onContentSelect={handleContentSelect}
            onContentUpdate={handleContentUpdate}
            onPreviewContent={handlePreviewContent}
            selectedContentId={state.selectedContentId}
          />
        );

      case 'editor':
        return (
          <ContentEditor
            contentId={state.selectedContentId}
            mode={state.selectedContentId ? 'edit' : 'create'}
            contentType="landing"
            onSave={content => handleContentUpdate(state.selectedContentId || '', content)}
            onCancel={() => setState(prev => ({ ...prev, activeTab: 'library' }))}
          />
        );

      case 'versions':
        return <VersionHistoryInterface contentId={state.selectedContentId} />;

      case 'approvals':
        return (
          <ApprovalWorkflowDashboard
            onWorkflowAction={handleWorkflowAction}
            onContentPreview={handlePreviewContent}
            refreshTrigger={state.notifications.length}
          />
        );

      case 'abtests':
        return <ABTestConfigWizard contentId={state.selectedContentId} />;

      case 'personalization':
        return <PersonalizationEditor contentId={state.selectedContentId} onPreview={handlePreviewContent} />;

      case 'cache':
        return <CacheManagementInterface />;

      default:
        return <div>Select a tab to get started</div>;
    }
  };

  if (state.loading) {
    return (
      <div className={`content-management-admin ${className}`}>
        <Grid>
          <Column lg={16}>
            <div style={{ marginBottom: '2rem' }}>
              <SkeletonText heading />
              <SkeletonText paragraph lineCount={2} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
              {[1, 2, 3, 4].map(i => (
                <SkeletonPlaceholder key={i} style={{ height: '120px' }} />
              ))}
            </div>
            <Loading />
          </Column>
        </Grid>
      </div>
    );
  }

  if (state.permissionError) {
    return (
      <div className={`content-management-admin ${className}`}>
        <Grid>
          <Column lg={16}>
            <InlineNotification kind="error" title="Access Denied" subtitle={state.permissionError} lowContrast />
            <div style={{ marginTop: '16px' }}>
              <p>
                Contact your system administrator to request marketing management permissions. Required role: Admin or
                Marketing Manager.
              </p>
            </div>
          </Column>
        </Grid>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={`content-management-admin ${className}`}>
        <Grid>
          <Column lg={16}>
            <InlineNotification
              kind="error"
              title="Content Management Error"
              subtitle={state.error}
              onCloseButtonClick={() => setState(prev => ({ ...prev, error: null }))}
            />
            <Button kind="primary" renderIcon={Document} onClick={loadDashboardData} style={{ marginTop: '1rem' }}>
              Retry Loading
            </Button>
          </Column>
        </Grid>
      </div>
    );
  }

  return (
    <Theme theme="g90">
      <div className={`content-management-admin ${className}`}>
        <Grid>
          <Column lg={16}>
            {/* Dashboard Header with Stats */}
            {renderDashboardHeader()}

            {/* Notifications */}
            {renderNotifications()}

            {/* Navigation Breadcrumb */}
            <Breadcrumb style={{ marginBottom: '2rem' }}>
              <BreadcrumbItem>
                <a href="/">Home</a>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <a href="/admin">Admin</a>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>Content Management</BreadcrumbItem>
            </Breadcrumb>

            {/* Main Content Tabs */}
            <Tabs onChange={handleTabChange}>
              <TabList>
                <Tab renderIcon={Document}>Content Library</Tab>
                <Tab renderIcon={Edit}>Content Editor</Tab>
                <Tab renderIcon={View}>Version History</Tab>
                <Tab renderIcon={Checkmark}>Approvals</Tab>
                <Tab renderIcon={Analytics}>A/B Tests</Tab>
                <Tab renderIcon={Settings}>Personalization</Tab>
                <Tab renderIcon={Notification}>Cache Management</Tab>
              </TabList>

              <TabPanels>
                <TabPanel style={{ padding: 0, marginTop: '2rem' }}>{renderTabContent()}</TabPanel>
                <TabPanel style={{ padding: 0, marginTop: '2rem' }}>{renderTabContent()}</TabPanel>
                <TabPanel style={{ padding: 0, marginTop: '2rem' }}>{renderTabContent()}</TabPanel>
                <TabPanel style={{ padding: 0, marginTop: '2rem' }}>{renderTabContent()}</TabPanel>
                <TabPanel style={{ padding: 0, marginTop: '2rem' }}>{renderTabContent()}</TabPanel>
                <TabPanel style={{ padding: 0, marginTop: '2rem' }}>{renderTabContent()}</TabPanel>
                <TabPanel style={{ padding: 0, marginTop: '2rem' }}>{renderTabContent()}</TabPanel>
              </TabPanels>
            </Tabs>
          </Column>
        </Grid>

        {/* Content Preview Modal */}
        {state.previewModalOpen && state.previewOptions && (
          <ContentPreviewModal
            open={state.previewModalOpen}
            onClose={() => setState(prev => ({ ...prev, previewModalOpen: false }))}
            previewOptions={state.previewOptions}
          />
        )}
      </div>
    </Theme>
  );
};

export default ContentManagementAdmin;
